import type {
  GapActionItems,
  GapCustomerFriction,
  GapDraftView,
  GapEscalation,
  GapProjectOverview,
  GapRoiImpact,
  GapRunPrecedent,
  GapSeenBefore,
} from '@momentum/types';

/**
 * The two-zone rule.
 *
 * A draft has two layers that never mix: `gap` is the product shape being
 * assembled, `source` is the run's own gap, frozen. The reader's PRODUCT zone
 * reads `gap.*`; when the field is unwritten — which today is nearly all of them
 * — it falls back to the `source` equivalent and says so on screen, and where
 * `source` has no equivalent it renders a placeholder naming what is missing and
 * which agent owes it. The PIPELINE zone (the inspector rail) always reads
 * `source` and never falls back.
 *
 * The fallback is the whole reason a developer can open a freshly-persisted
 * draft and read it at all, and the "from run" tag is what stops them mistaking
 * the run's raw prose for the enriched output that will eventually replace it.
 *
 * Every resolver returns a discriminated union rather than a
 * `{ value, from }` pair: the gap-layer and run-layer values have different
 * shapes for most fields (`projectOverview` is an object, its run equivalent is
 * prose), so a single `value` would have to be a union the caller then narrows
 * by hand.
 *
 * The gap layer has two authors, so `from` tells them apart. A human's edit is
 * written into `gap` in place — there is no third layer to read it from — and
 * `editedPaths` is what the draft carries to say so.
 */

/** Which layer, and which author, a rendered value came from. */
export type ValueOrigin = 'gap' | 'human' | 'run' | 'run-note' | 'missing';

/** The gap layer's two authors: the agents, or a reviewer who edited it. */
export type GapAuthor = 'gap' | 'human';

/** The lede: the enriched project overview, or the run's two prose blocks. */
export type LedeValue =
  | { from: GapAuthor; projectOverview: GapProjectOverview }
  | { from: 'run'; text: string };

/** A field both layers carry as prose. */
export type ProseValue = { from: GapAuthor | 'run'; text: string };

/** A field only the gap layer can carry. */
export type GapOnlyValue<TValue> =
  | { from: GapAuthor; value: TValue }
  | { from: 'missing' };

export type SeenBeforeValue =
  | { from: GapAuthor; seenBefore: GapSeenBefore }
  | { from: 'run'; precedents: GapRunPrecedent[] }
  | { from: 'run-note'; note: string }
  | { from: 'missing' };

/**
 * Whether a human has edited this path, or anything under it.
 *
 * Prefix matching is what lets a whole section answer: `gap.projectOverview`
 * is edited when any of its four prose fields is, which is the granularity the
 * reader's lede is rendered at.
 */
export function hasHumanEdit(draft: GapDraftView, path: string): boolean {
  return draft.editedPaths.some(
    (editedPath) => editedPath === path || editedPath.startsWith(`${path}.`)
  );
}

function gapAuthor(draft: GapDraftView, path: string): GapAuthor {
  return hasHumanEdit(draft, path) ? 'human' : 'gap';
}

/**
 * The run has no single overview — it has an account narrative and a project
 * narrative, written to be read one after the other — so the fallback joins them
 * rather than picking one.
 */
export function resolveLede(draft: GapDraftView): LedeValue {
  if (draft.gap.projectOverview) {
    return {
      from: gapAuthor(draft, 'gap.projectOverview'),
      projectOverview: draft.gap.projectOverview,
    };
  }
  return {
    from: 'run',
    text: [draft.source.accountOverview, draft.source.projectOverview]
      .filter(Boolean)
      .join('\n\n'),
  };
}

/**
 * The gap statement is `projectOverview.gapSummary` — the only field that
 * states what is missing — so the claim and the lede resolve off the same
 * subdocument, at different granularities.
 */
export function resolveClaim(draft: GapDraftView): ProseValue {
  const gapSummary = draft.gap.projectOverview?.gapSummary;
  return gapSummary !== undefined
    ? {
        from: gapAuthor(draft, 'gap.projectOverview.gapSummary'),
        text: gapSummary,
      }
    : { from: 'run', text: draft.source.claim };
}

export function resolveFullProjectContext(draft: GapDraftView): ProseValue {
  return draft.gap.fullProjectContext !== undefined
    ? {
        from: gapAuthor(draft, 'gap.fullProjectContext'),
        text: draft.gap.fullProjectContext,
      }
    : { from: 'run', text: draft.source.fullProjectContext };
}

/**
 * Three fallbacks deep, because "we looked and found nothing" is a real answer
 * that has to be told apart from "nobody looked": the run's own
 * `precedentSearchNote` is the disclosure behind an empty `precedents`, and it
 * renders as prose rather than as a precedent list.
 */
export function resolveSeenBefore(draft: GapDraftView): SeenBeforeValue {
  if (draft.gap.seenBefore) {
    return {
      from: gapAuthor(draft, 'gap.seenBefore'),
      seenBefore: draft.gap.seenBefore,
    };
  }
  if (draft.source.precedents?.length) {
    return { from: 'run', precedents: draft.source.precedents };
  }
  if (draft.source.precedentSearchNote) {
    return { from: 'run-note', note: draft.source.precedentSearchNote };
  }
  return { from: 'missing' };
}

/**
 * The run records a single drift point, not a history, so there is no run
 * equivalent of the escalation timeline to fall back to. The reader shows the
 * drift point beside the placeholder instead — as one dated fact, labelled as
 * one — so the section is not simply blank.
 */
export function resolveEscalation(
  draft: GapDraftView
): GapOnlyValue<GapEscalation> {
  return gapOnly(draft, 'gap.escalation', draft.gap.escalation);
}

export function resolvePotentialImpact(
  draft: GapDraftView
): GapOnlyValue<string> {
  return gapOnly(draft, 'gap.potentialImpact', draft.gap.potentialImpact);
}

export function resolveRoiImpact(
  draft: GapDraftView
): GapOnlyValue<GapRoiImpact> {
  return gapOnly(draft, 'gap.roiImpact', draft.gap.roiImpact);
}

export function resolveActionItems(
  draft: GapDraftView
): GapOnlyValue<GapActionItems> {
  return gapOnly(draft, 'gap.actionItems', draft.gap.actionItems);
}

/**
 * Severity is a 0–1 business-impact score, and the only field on this page that
 * cannot be faked from the run: `source.impact` is a LOW/MEDIUM/HIGH finder
 * score on a different question, and mapping one onto the other would invent a
 * number the pipeline never produced.
 */
export function resolveSeverity(draft: GapDraftView): GapOnlyValue<number> {
  return gapOnly(draft, 'gap.severity', draft.gap.severity);
}

/**
 * Missability is severity's twin: a 0–1 score the enrichment recomputes, and
 * gap-only for the same reason. `source.missability` is a LOW/MEDIUM/HIGH/
 * EXTREME finder enum on the same question, scored to sort candidates rather
 * than to rank a product, so it is not a fallback either.
 */
export function resolveMissability(draft: GapDraftView): GapOnlyValue<number> {
  return gapOnly(draft, 'gap.missability', draft.gap.missability);
}

/**
 * Customer friction is the third score the enrichment recomputes, and gap-only
 * like the other two — but for a different reason. `source.customerFriction`
 * IS on the same question, and it is still not a fallback: the finder scores
 * three levels with no `NONE` against its own one-line `doNow`, so its `LOW`
 * covers both "nothing reaches the customer" and "something does". Falling back
 * to it would put a level on screen that means something else, under a label
 * saying what the enriched one means. The rail shows the run's reading instead,
 * where it belongs.
 */
export function resolveCustomerFriction(
  draft: GapDraftView
): GapOnlyValue<GapCustomerFriction> {
  return gapOnly(draft, 'gap.customerFriction', draft.gap.customerFriction);
}

function gapOnly<TValue>(
  draft: GapDraftView,
  path: string,
  value: TValue | undefined
): GapOnlyValue<TValue> {
  return value !== undefined
    ? { from: gapAuthor(draft, path), value }
    : { from: 'missing' };
}
